import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import RatiosReportDialog from "../components/RatiosReportDialog";

export default function RatiosPage({ deviceType = "analyzer" }) {
  const { t, i18n } = useTranslation();
  const { deviceId } = useParams();
  const [dailyRatios, setDailyRatios] = useState(null);
  const [summaryRatios, setSummaryRatios] = useState(null);
  const [selectedRange, setSelectedRange] = useState("daily");
  const [reportOpen, setReportOpen] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const supportsRangeSelection =
    deviceType === "reactive" || deviceType === "gruparge";
  const ENDPOINT_PREFIX =
    deviceType === "gruparge"
      ? "/gruparge"
      : deviceType === "reactive"
        ? "/reactive"
        : "/measurements";

  useEffect(() => {
    async function fetchData() {
      try {
        const rangeParam =
          supportsRangeSelection ? `&range=${selectedRange}` : "";

        const [historyRes, summaryRes] = await Promise.all([
          fetch(`${API_BASE}${ENDPOINT_PREFIX}/daily-ratios-history?deviceId=${deviceId}${rangeParam}`),
          supportsRangeSelection
            ? fetch(`${API_BASE}${ENDPOINT_PREFIX}/ratios?deviceId=${deviceId}`)
            : Promise.resolve(null),
        ]);

        if (!historyRes.ok) throw new Error(`HTTP ${historyRes.status}`);
        const historyJson = await historyRes.json();
        setDailyRatios(historyJson);

        if (summaryRes) {
          if (!summaryRes.ok) throw new Error(`HTTP ${summaryRes.status}`);
          const summaryJson = await summaryRes.json();
          setSummaryRatios(summaryJson);
        }
      } catch (err) {
        console.error("fetch daily ratios error:", err);
      }
    }

    if (deviceId) {
      fetchData();
      const i = setInterval(fetchData, 30000);
      return () => clearInterval(i);
    }
  }, [deviceId, API_BASE, ENDPOINT_PREFIX, deviceType, selectedRange, supportsRangeSelection]);

  if (!dailyRatios) return <p>{t("common.loading") || "Loading..."}</p>;
  const summaryIndRatio = Number(summaryRatios?.tanPhiEnd);
  const summaryCapRatio = Math.abs(Number(summaryRatios?.tanPhiCap));
  const hasSummaryRatios =
    Number.isFinite(summaryIndRatio) && Number.isFinite(summaryCapRatio);

  // The device's achieved-percentage registers are the authoritative lifetime
  // values. The history endpoint derives ratios from energy-counter deltas,
  // which can be unavailable on some devices and used to produce a false 0%.
  const normalizedRatios =
    supportsRangeSelection && selectedRange === "total" && hasSummaryRatios
      ? dailyRatios.map((row) => {
          const isInductivePenalty = summaryIndRatio > 20;
          const isCapacitivePenalty = summaryCapRatio > 15;
          let penalty = "NONE";
          if (isInductivePenalty && isCapacitivePenalty) penalty = "BOTH";
          else if (isInductivePenalty) penalty = "INDUCTIVE";
          else if (isCapacitivePenalty) penalty = "CAPACITIVE";

          return {
            ...row,
            indRatio: summaryIndRatio,
            capRatio: summaryCapRatio,
            isInductivePenalty,
            isCapacitivePenalty,
            penalty,
          };
        })
      : dailyRatios;
  const displayRows =
    supportsRangeSelection ? [...normalizedRatios].reverse() : normalizedRatios;

  const uiLocaleMap = {
    tr: "tr-TR",
    en: "en-GB",
    de: "de-DE",
    fr: "fr-FR",
    es: "es-ES",
    ru: "ru-RU",
  };
  const uiLocale = uiLocaleMap[i18n.language] || "en-US";

  const formatReactivePeriod = (row) => {
    if (row.range === "total") return t("ratios.rangeTotalFromFirst");
    if (!row.periodStart) return "-";

    const start = new Date(row.periodStart);
    if (row.range === "daily") {
      const dateText = new Intl.DateTimeFormat(uiLocale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(start);
      const dayName = new Intl.DateTimeFormat(uiLocale, {
        weekday: "long",
      }).format(start);
      return `${dayName} - ${dateText}`;
    }

    if (row.range === "weekly") {
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const fmt = new Intl.DateTimeFormat(uiLocale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      return `${fmt.format(start)} - ${fmt.format(end)}`;
    }

    if (row.range === "monthly") {
      return new Intl.DateTimeFormat(uiLocale, {
        month: "long",
        year: "numeric",
      }).format(start);
    }

    return "-";
  };

  const getPenaltyLabel = (penalty) => {
    switch (penalty) {
      case "INDUCTIVE":
        return <span className="text-red-600 font-semibold">{t("ratios.penaltyInductive") || "Enduktif Ceza"}</span>;
      case "CAPACITIVE":
        return <span className="text-orange-600 font-semibold">{t("ratios.penaltyCapacitive") || "Kapasitif Ceza"}</span>;
      case "BOTH":
        return <span className="text-red-700 font-bold">{t("ratios.penaltyBoth") || "Her Ikisi"}</span>;
      default:
        return <span className="text-green-600">{t("ratios.noPenalty") || "OK"}</span>;
    }
  };

  const selectedRangeLabel = {
    daily: t("ratios.rangeDaily"),
    weekly: t("ratios.rangeWeekly"),
    monthly: t("ratios.rangeMonthly"),
    total: t("ratios.rangeTotal"),
  }[selectedRange];

  return (
    <div className="p-4 md:p-6 bg-white rounded-xl shadow-md">
      <h2 className="text-lg font-bold mb-4 md:mb-6 text-gray-800">
        {supportsRangeSelection
          ? `${selectedRangeLabel} — ${t("sidebar.ratios")}`
          : (t("ratios.dailyTitle") || "Gunluk Oranlar")}
      </h2>

      {supportsRangeSelection && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1">
                {t("ratios.totalInductiveRatio") || "Total Inductive Ratio"}
              </div>
              <div className={`text-2xl font-bold ${summaryIndRatio > 20 ? "text-red-700" : "text-green-700"}`}>
                {hasSummaryRatios ? `%${summaryIndRatio.toFixed(2)}` : "—"}
              </div>
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1">
                {t("ratios.totalCapacitiveRatio") || "Total Capacitive Ratio"}
              </div>
              <div className={`text-2xl font-bold ${summaryCapRatio > 15 ? "text-red-700" : "text-green-700"}`}>
                {hasSummaryRatios ? `%${summaryCapRatio.toFixed(2)}` : "—"}
              </div>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            {[
              { key: "daily", label: t("ratios.rangeDaily") },
              { key: "weekly", label: t("ratios.rangeWeekly") },
              { key: "monthly", label: t("ratios.rangeMonthly") },
              { key: "total", label: t("ratios.rangeTotal") },
            ].map((opt) => {
              const active = selectedRange === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSelectedRange(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-sm border ${
                    active
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[300px]">
          <thead>
            <tr className="bg-indigo-50 text-gray-700">
              <th className="border p-2 md:p-3 text-left">
                {supportsRangeSelection ? t("ratios.period") : (t("ratios.date") || "Tarih")}
              </th>
              <th className="border p-2 md:p-3 text-center">{t("ratios.totalInductiveRatio") || "Total Inductive Ratio"}</th>
              <th className="border p-2 md:p-3 text-center">{t("ratios.totalCapacitiveRatio") || "Total Capacitive Ratio"}</th>
              <th className="border p-2 md:p-3 text-center">{t("ratios.penalty") || "Ceza Durumu"}</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, idx) => {
              const isToday = supportsRangeSelection
                ? idx === 0
                : idx === displayRows.length - 1;
              const rowClass = isToday
                ? "bg-blue-50 font-semibold"
                : row.penalty !== "NONE"
                  ? "bg-red-50 hover:bg-red-100"
                  : "hover:bg-gray-50";

              return (
                <tr key={row.periodStart || row.fullDate || row.range || idx} className={rowClass}>
                  <td className="border p-2 md:p-3 font-medium">
                    {supportsRangeSelection ? formatReactivePeriod(row) : row.date}
                    {!supportsRangeSelection && isToday && (
                      <span className="ml-2 text-xs text-blue-600">
                        ({t("ratios.today") || "Bugun"})
                      </span>
                    )}
                  </td>
                  <td className="border p-2 md:p-3 text-center text-green-600 font-semibold">
                    %{Number(row.indRatio || 0).toFixed(3)}
                  </td>
                  <td className="border p-2 md:p-3 text-center text-red-600 font-semibold">
                    %{Number(row.capRatio || 0).toFixed(3)}
                  </td>
                  <td className="border p-2 md:p-3 text-center">
                    {getPenaltyLabel(row.penalty)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-bold mb-2 text-gray-700">{t("ratios.limits") || "Ceza Limitleri"}</h3>
        <div className="text-xs text-gray-600 space-y-1">
          <p>- {t("ratios.inductiveLimit") || "Enduktif Limit"}: <span className="font-semibold">%20</span></p>
          <p>- {t("ratios.capacitiveLimit") || "Kapasitif Limit"}: <span className="font-semibold">%15</span></p>
        </div>
      </div>

      <div className="flex justify-start mt-4">
        <button
          onClick={() => setReportOpen(true)}
          className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 text-sm"
        >
          {t("ratios.reportButton")}
        </button>
      </div>

      <RatiosReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        deviceId={deviceId}
        deviceType={deviceType}
        API_BASE={API_BASE}
      />
    </div>
  );
}
